import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lock, Shield, Fingerprint, KeyRound, Loader2, Users, Plus, Trash2, Pencil, Paperclip, AlertTriangle, FileText, ShieldCheck, Search, Infinity, Sparkles, Download, Eye, FolderOpen, Upload, Briefcase, FileCheck2, HardHat, Stethoscope, IdCard } from "lucide-react";
import { toast } from "sonner";

const emptyDip = { nome: "", cognome: "", codice_fiscale: "", telefono: "", email: "", contratto_tipo: "", contratto_scadenza: "", patente_scadenza: "", visita_medica_scadenza: "", note: "", user_id: "" };

// Categorie Fascicolo Dipendente (HR Digital Dossier)
const CATEGORIES = [
  { key: "contratti", label: "Contratti e UNILAV", desc: "Assunzioni, Proroghe, Trasformazioni", icon: Briefcase, aiEnabled: true },
  { key: "buste_paga", label: "Buste Paga", desc: "Cedolini mensili", icon: FileCheck2, aiEnabled: false },
  { key: "sicurezza", label: "Sicurezza e Formazione", desc: "Patentini, Corsi Sicurezza, Consegna DPI", icon: HardHat, aiEnabled: false },
  { key: "medico", label: "Visite Mediche e Idoneità", desc: "Certificati medici, idoneità", icon: Stethoscope, aiEnabled: false },
  { key: "identita", label: "Documenti Identità", desc: "Carta Identità, Patente Guida", icon: IdCard, aiEnabled: true },
];
const CAT_BY_KEY = Object.fromEntries(CATEGORIES.map((c) => [c.key, c]));

const isIndeterminato = (s) => {
  const v = (s || "").toLowerCase();
  return v.includes("indeterminato") || v === "ti";
};
const renderContrattoCell = (d) => {
  if (!d.contratto_tipo) return "—";
  if (isIndeterminato(d.contratto_tipo)) {
    return (
      <span className="flex items-center gap-1" data-testid={`hr-contratto-indeterminato`}>
        <Infinity className="h-4 w-4 text-emerald-600" />
        <span className="text-emerald-700 font-medium">Tempo indeterminato</span>
      </span>
    );
  }
  return <span>{d.contratto_tipo}</span>;
};

// --- WebAuthn helpers: base64url ↔ ArrayBuffer ---
function _b64urlToBytes(s) {
  s = String(s).replace(/-/g, "+").replace(/_/g, "/");
  s += "=".repeat((4 - (s.length % 4)) % 4);
  const bin = atob(s);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}
function _bytesToB64url(buf) {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}
function _creationOpts(o) {
  return {
    ...o,
    challenge: _b64urlToBytes(o.challenge).buffer,
    user: { ...o.user, id: _b64urlToBytes(o.user.id).buffer },
    excludeCredentials: (o.excludeCredentials || []).map((x) => ({ ...x, id: _b64urlToBytes(x.id).buffer })),
  };
}
function _requestOpts(o) {
  return {
    ...o,
    challenge: _b64urlToBytes(o.challenge).buffer,
    allowCredentials: (o.allowCredentials || []).map((x) => ({ ...x, id: _b64urlToBytes(x.id).buffer })),
  };
}
function _packCreateResp(c) {
  const r = c.response;
  return {
    id: c.id,
    rawId: _bytesToB64url(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment || null,
    clientExtensionResults: c.getClientExtensionResults ? c.getClientExtensionResults() : {},
    response: {
      clientDataJSON: _bytesToB64url(r.clientDataJSON),
      attestationObject: _bytesToB64url(r.attestationObject),
      transports: r.getTransports ? r.getTransports() : [],
    },
  };
}
function _packGetResp(c) {
  const r = c.response;
  return {
    id: c.id,
    rawId: _bytesToB64url(c.rawId),
    type: c.type,
    authenticatorAttachment: c.authenticatorAttachment || null,
    clientExtensionResults: c.getClientExtensionResults ? c.getClientExtensionResults() : {},
    response: {
      clientDataJSON: _bytesToB64url(r.clientDataJSON),
      authenticatorData: _bytesToB64url(r.authenticatorData),
      signature: _bytesToB64url(r.signature),
      userHandle: r.userHandle ? _bytesToB64url(r.userHandle) : null,
    },
  };
}

export default function ArchivioHR() {
  const [unlocked, setUnlocked] = useState(false);
  const [busy, setBusy] = useState(false);
  const [list, setList] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [usersAvailable, setUsersAvailable] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyDip);
  const [editId, setEditId] = useState(null);
  const [detail, setDetail] = useState(null); // dipendente selezionato per allegati
  const [docForm, setDocForm] = useState({ nome: "", tipo: "", scadenza: "", file_base64: "" });
  // Fascicolo Dipendente: AI parsing + category tab
  const [activeCat, setActiveCat] = useState("contratti");
  const [parseBusy, setParseBusy] = useState(false);
  const [parsedOpen, setParsedOpen] = useState(false);
  const [parsedPreview, setParsedPreview] = useState(null);
  const [previewDoc, setPreviewDoc] = useState(null); // {file_base64, nome, tipo}
  // WebAuthn + password fallback state
  const [hasPasskey, setHasPasskey] = useState(null); // null = sconosciuto
  const [passwordMode, setPasswordMode] = useState(false);
  const [password, setPassword] = useState("");
  const biometricSupported = typeof window !== "undefined" && !!window.PublicKeyCredential;
  // Filtri
  const [search, setSearch] = useState("");
  const [filterColumn, setFilterColumn] = useState("contratto_scadenza"); // su quale data filtrare
  const [filterMese, setFilterMese] = useState(0); // 0 = tutti
  const [filterAnno, setFilterAnno] = useState(0); // 0 = tutti
  const [filterDa, setFilterDa] = useState("");
  const [filterA, setFilterA] = useState("");

  const filteredList = useMemo(() => {
    const q = search.trim().toLowerCase();
    return list.filter((d) => {
      if (q) {
        const full = `${d.nome || ""} ${d.cognome || ""}`.toLowerCase();
        if (!full.includes(q)) return false;
      }
      const val = d[filterColumn];
      const needsDate = filterMese || filterAnno || filterDa || filterA;
      if (needsDate) {
        if (!val) return false;
        const dt = String(val);
        if (filterAnno && !dt.startsWith(String(filterAnno))) return false;
        if (filterMese) {
          const m = parseInt(dt.slice(5, 7), 10);
          if (m !== filterMese) return false;
        }
        if (filterDa && dt < filterDa) return false;
        if (filterA && dt > filterA) return false;
      }
      return true;
    });
  }, [list, search, filterColumn, filterMese, filterAnno, filterDa, filterA]);

  const anniDisponibili = useMemo(() => {
    const set = new Set();
    list.forEach((d) => {
      ["contratto_scadenza", "patente_scadenza", "visita_medica_scadenza"].forEach((k) => {
        if (d[k] && d[k].length >= 4) set.add(d[k].slice(0, 4));
      });
    });
    return Array.from(set).sort().reverse();
  }, [list]);

  const anyFilter = search || filterMese || filterAnno || filterDa || filterA;

  const [errorBanner, setErrorBanner] = useState(null);
  const load = async () => {
    setErrorBanner(null);
    try {
      const [d, a, u] = await Promise.all([
        api.get("/hr/dipendenti"),
        api.get("/hr/alerts").catch(() => ({ data: [] })),
        api.get("/hr/users-available").catch(() => ({ data: [] })),
      ]);
      setList(Array.isArray(d.data) ? d.data : []);
      setAlerts(Array.isArray(a.data) ? a.data : []);
      setUsersAvailable(Array.isArray(u.data) ? u.data : []);
      setUnlocked(true);
    } catch (e) {
      if (e?.response?.status === 423) {
        setUnlocked(false);
        // Scopri se l'admin ha già registrato una passkey
        try { const { data } = await api.get("/webauthn/credentials/status"); setHasPasskey(!!data.has_passkey); }
        catch { setHasPasskey(false); }
      } else {
        // Non lasciare schermo nero: mostra banner e mantieni pannello interattivo
        const msg = e?.response?.data?.detail || e?.message || "Errore di caricamento dati HR.";
        setErrorBanner(String(msg).slice(0, 300));
        setUnlocked(true);
        setList([]); setAlerts([]); setUsersAvailable([]);
        toast.error("Errore caricamento Archivio HR");
      }
    }
  };
  useEffect(() => { load(); }, []);

  const unlockWithBiometric = async () => {
    if (!biometricSupported) {
      toast.error("Questo dispositivo non supporta l'autenticazione biometrica. Usa la password.");
      setPasswordMode(true);
      return;
    }
    if (!hasPasskey) {
      toast.info("Nessuna passkey registrata per questo account. Clicca 'Registra questo dispositivo' qui sotto.");
      return;
    }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/webauthn/authentication/options", {});
      const cred = await navigator.credentials.get({ publicKey: _requestOpts(opts) });
      if (!cred) throw new Error("Nessuna credenziale restituita");
      await api.post("/webauthn/authentication/verify", _packGetResp(cred));
      toast.success("Accesso HR sbloccato (30 min)");
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      const errName = e?.name || "";
      const msg = String(e?.message || "");
      // Caso tipico mobile: utente ha passkey su altro dispositivo ma non su questo.
      // Il browser dice "nessuna passkey trovata per questo dominio su questo dispositivo".
      // NON forziamo la password: suggeriamo di REGISTRARE una passkey per questo dispositivo.
      if (errName === "NotAllowedError" || /no.*passkey|credential.*not.*available|not.*registered/i.test(msg + detail)) {
        toast.info("Questo dispositivo non ha ancora una passkey. Clicca 'Registra questo dispositivo' qui sotto, oppure usa la password.", { duration: 8000 });
      } else if (["SecurityError", "NotSupportedError", "InvalidStateError", "AbortError"].includes(errName)) {
        toast.info("Autenticazione biometrica non completata. Riprova oppure usa la password amministratore.");
      } else {
        toast.error(detail || msg || "Autenticazione biometrica non riuscita");
      }
    } finally {
      setBusy(false);
    }
  };

  const registerPasskey = async () => {
    if (!biometricSupported) {
      toast.error("Dispositivo non supporta biometria. Usa la password.");
      setPasswordMode(true);
      return;
    }
    setBusy(true);
    try {
      const { data: opts } = await api.post("/webauthn/registration/options", {});
      const cred = await navigator.credentials.create({ publicKey: _creationOpts(opts) });
      if (!cred) throw new Error("Nessuna credenziale creata");
      await api.post("/webauthn/registration/verify", _packCreateResp(cred));
      toast.success("Dispositivo registrato · Archivio HR sbloccato (30 min)");
      setHasPasskey(true);
      load();
    } catch (e) {
      const errName = e?.name || "";
      const detail = e?.response?.data?.detail || "";
      const msg = String(e?.message || "");
      if (errName === "InvalidStateError" || /already.*registered|already enrolled/i.test(msg + detail)) {
        toast.info("Questo dispositivo risulta già registrato. Clicca 'Sblocca con Impronta' per autenticare.", { duration: 7000 });
        setHasPasskey(true);
      } else if (errName === "NotAllowedError") {
        toast.info("Registrazione annullata. Riprova e autorizza il sensore biometrico quando richiesto.");
      } else if (["SecurityError", "NotSupportedError", "AbortError"].includes(errName)) {
        toast.info("Il browser mobile non ha completato la registrazione. Puoi usare la password amministratore oppure riprovare.");
      } else {
        toast.error(detail || msg || "Errore registrazione impronta");
      }
    } finally {
      setBusy(false);
    }
  };

  const unlockWithPassword = async () => {
    if (!password.trim()) return toast.error("Inserisci la password amministratore");
    setBusy(true);
    try {
      await api.post("/hr/password-unlock", { password });
      toast.success("Accesso HR sbloccato (30 min)");
      setPassword("");
      setPasswordMode(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Password errata");
    } finally {
      setBusy(false);
    }
  };

  const save = async () => {
    if (!form.nome.trim()) return toast.error("Inserisci nome");
    const payload = {
      ...form,
      contratto_scadenza: form.contratto_scadenza || null,
      patente_scadenza: form.patente_scadenza || null,
      visita_medica_scadenza: form.visita_medica_scadenza || null,
    };
    const linkTargetUserId = form.user_id || "";
    // user_id non è un campo del modello DipendenteHRIn: non va inviato al PUT/POST
    delete payload.user_id;
    try {
      let targetId = editId;
      if (editId) {
        await api.put(`/hr/dipendenti/${editId}`, payload);
      } else {
        const { data } = await api.post("/hr/dipendenti", payload);
        targetId = data?.id;
      }
      // Aggiorna collegamento utente (bidirezionale, idempotente)
      const currentUserId = (list.find((x) => x.id === editId)?.user_id) || "";
      if (targetId && linkTargetUserId !== currentUserId) {
        try { await api.post(`/hr/dipendenti/${targetId}/link-user`, { user_id: linkTargetUserId || null }); }
        catch (e) { toast.error(e?.response?.data?.detail || "Errore collegamento utente"); }
      }
      toast.success(editId ? "Dipendente aggiornato" : "Dipendente aggiunto");
      setForm(emptyDip); setEditId(null); setOpen(false); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante il salvataggio"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo dipendente e i suoi documenti?")) return;
    try { await api.delete(`/hr/dipendenti/${id}`); toast.success("Eliminato"); load(); } catch { toast.error("Errore"); }
  };

  const openEdit = (d) => { setEditId(d.id); setForm({ ...emptyDip, ...d, user_id: d.user_id || "" }); setOpen(true); };

  const uploadDoc = async (file, categoria = "altro", meta = {}) => {
    if (!file) return;
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = reader.result;
        try {
          await api.post(`/hr/dipendenti/${detail.id}/documento`, {
            nome: meta.nome || file.name,
            tipo: meta.tipo || "",
            categoria: categoria || "altro",
            scadenza: meta.scadenza || null,
            file_base64: b64,
          });
          resolve({ ok: true, b64 });
        } catch (e) {
          toast.error(e?.response?.data?.detail || `Errore upload ${file.name}`);
          resolve({ ok: false });
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const uploadFilesToCategory = async (filesList, categoria) => {
    if (!filesList || filesList.length === 0) return;
    const files = Array.from(filesList);
    setBusy(true);
    try {
      // STEP 1: carica SEMPRE i file nella categoria scelta (robust persistence).
      // Qualunque errore di AI parsing successivo non deve far perdere il file.
      let savedCount = 0;
      const savedFiles = [];
      for (const f of files) {
        const r = await uploadDoc(f, categoria, { nome: f.name });
        if (r?.ok) {
          savedCount += 1;
          savedFiles.push(f);
        }
      }
      const catLabel = CATEGORIES.find((c) => c.key === categoria)?.label || categoria;
      if (savedCount === 0) {
        toast.error("Nessun file caricato");
        return;
      }
      toast.success(`${savedCount} documento/i caricati in ${catLabel}`);
      // Aggiorna lista dopo upload
      const { data } = await api.get("/hr/dipendenti");
      setList(data);
      const freshDetail = data.find((x) => x.id === detail.id);
      setDetail(freshDetail);
      // STEP 2: AI parsing SOLO per Contratti/Identità, sul primo file salvato con successo.
      // Il modal di review si apre SEMPRE in queste categorie, con dati pre-filled o vuoti.
      const aiCategories = ["contratti", "identita"];
      if (!aiCategories.includes(categoria) || !savedFiles[0]) return;
      let parsedData = null;
      let aiFailed = false;
      const first = savedFiles[0];
      toast.info(`AI sta leggendo "${first.name}"...`, { duration: 15000, id: "hr-ai" });
      setParseBusy(true);
      try {
        const b64 = await new Promise((res) => {
          const r = new FileReader();
          r.onload = () => res(r.result);
          r.readAsDataURL(first);
        });
        const { data: ai } = await api.post("/hr/parse-document", { file_base64: b64, categoria });
        parsedData = ai;
        toast.dismiss("hr-ai");
      } catch (e) {
        aiFailed = true;
        toast.dismiss("hr-ai");
        toast.info("AI non ha estratto dati. Il file è salvato — compila i campi manualmente.");
      } finally { setParseBusy(false); }
      // Apri SEMPRE il review modal (per entrambi i casi: AI OK o manuale)
      setParsedPreview({
        nome: parsedData?.nome || "",
        cognome: parsedData?.cognome || "",
        codice_fiscale: (parsedData?.codice_fiscale || "").toUpperCase(),
        contratto_tipo: parsedData?.contratto_tipo || "",
        contratto_scadenza: parsedData?.contratto_scadenza || "",
        documento_tipo: parsedData?.documento_tipo || "",
        numero_documento: parsedData?.numero_documento || "",
        data_scadenza_documento: parsedData?.data_scadenza_documento || "",
        _employee_id: detail.id,
        _current: freshDetail,
        _ai_failed: aiFailed,
        _source_file: first.name,
      });
      setParsedOpen(true);
    } finally {
      setBusy(false);
    }
  };

  const applyParsedData = async () => {
    if (!parsedPreview || !parsedPreview._employee_id) return;
    const current = parsedPreview._current || detail;
    const payload = {
      nome: parsedPreview.nome || current?.nome || "",
      cognome: parsedPreview.cognome || current?.cognome || "",
      codice_fiscale: (parsedPreview.codice_fiscale || current?.codice_fiscale || "").toUpperCase(),
      telefono: current?.telefono || "",
      email: current?.email || "",
      contratto_tipo: parsedPreview.contratto_tipo || current?.contratto_tipo || "",
      contratto_scadenza: parsedPreview.contratto_scadenza || current?.contratto_scadenza || null,
      patente_scadenza: parsedPreview.data_scadenza_documento && /patente/i.test(parsedPreview.documento_tipo || "")
        ? parsedPreview.data_scadenza_documento
        : (current?.patente_scadenza || null),
      visita_medica_scadenza: current?.visita_medica_scadenza || null,
      note: current?.note || "",
    };
    try {
      await api.put(`/hr/dipendenti/${parsedPreview._employee_id}`, payload);
      toast.success("Dati applicati al dipendente");
      setParsedOpen(false); setParsedPreview(null);
      await load();
      const refreshed = (await api.get("/hr/dipendenti")).data;
      setDetail(refreshed.find((x) => x.id === parsedPreview._employee_id));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore aggiornamento");
    }
  };

  const removeDoc = async (docId) => {
    if (!window.confirm("Eliminare questo documento?")) return;
    try {
      await api.delete(`/hr/dipendenti/${detail.id}/documento/${docId}`);
      const { data } = await api.get("/hr/dipendenti");
      setList(data);
      setDetail(data.find((x) => x.id === detail.id));
      toast.success("Rimosso");
    } catch { toast.error("Errore"); }
  };

  if (!unlocked) {
    // IMPORTANTE: le passkey sono per-dispositivo. `hasPasskey` indica solo se
    // ESISTE una passkey per l'utente nel database (es. registrata dal desktop).
    // Un nuovo smartphone deve registrare la PROPRIA passkey locale.
    const hasAnyPasskeyOnAccount = !!hasPasskey;
    return (
      <div className="max-w-md mx-auto space-y-6 py-12">
        <div className="text-center space-y-2">
          <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10"><Lock className="h-8 w-8 text-primary" /></div>
          <h1 className="text-2xl font-extrabold">Archivio HR protetto</h1>
          <p className="text-sm text-muted-foreground">
            {biometricSupported
              ? "Autentica con l'impronta registrata su QUESTO dispositivo, oppure registra una nuova impronta se è la prima volta che apri l'Archivio HR da qui."
              : "Questo dispositivo non supporta la biometria. Usa la password amministratore."}
          </p>
        </div>
        <Card className="card-elevated"><CardContent className="p-6 space-y-4">
          {!passwordMode ? (
            <>
              {biometricSupported && hasAnyPasskeyOnAccount && (
                <Button data-testid="hr-biometric-btn" onClick={unlockWithBiometric} disabled={busy} className="w-full h-12 text-base">
                  {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Fingerprint className="h-5 w-5 mr-2" />}
                  Sblocca con Impronta
                </Button>
              )}
              {biometricSupported && (
                <>
                  <div className="text-[11px] text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/20 border border-sky-200 rounded p-2 flex items-start gap-2">
                    <Fingerprint className="h-3 w-3 mt-0.5 shrink-0 text-sky-600" />
                    <span>
                      {hasAnyPasskeyOnAccount
                        ? "Nuovo smartphone o tablet? Registra una passkey per QUESTO dispositivo. Puoi averne più di una, una per ciascun dispositivo."
                        : "Prima configurazione: registra la tua impronta per questo dispositivo. Serve solo una volta per dispositivo."}
                    </span>
                  </div>
                  <Button data-testid="hr-register-biometric-btn" variant="outline" onClick={registerPasskey} disabled={busy} className="w-full h-11 text-sm border-primary/40 hover:bg-primary/5">
                    {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Fingerprint className="h-4 w-4 mr-2 text-primary" />}
                    {hasAnyPasskeyOnAccount ? "Registra questo dispositivo" : "Registra impronta per questo dispositivo"}
                  </Button>
                </>
              )}
              <div className="relative flex items-center">
                <div className="flex-1 border-t" />
                <span className="px-2 text-[10px] text-muted-foreground uppercase">oppure</span>
                <div className="flex-1 border-t" />
              </div>
              <button
                type="button"
                data-testid="hr-use-password-link"
                onClick={() => setPasswordMode(true)}
                className="w-full text-sm text-muted-foreground hover:text-primary underline underline-offset-2 flex items-center justify-center gap-1 py-1"
              >
                <KeyRound className="h-3 w-3" />
                Usa Password Amministratore
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <KeyRound className="h-4 w-4" /> Inserisci la tua password di login amministratore.
              </div>
              <div>
                <Label htmlFor="hr-password-input">Password amministratore</Label>
                <Input
                  id="hr-password-input"
                  data-testid="hr-password-input"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && unlockWithPassword()}
                  autoFocus
                  autoComplete="current-password"
                  placeholder="••••••••"
                  className="font-mono"
                />
              </div>
              <Button data-testid="hr-password-submit" onClick={unlockWithPassword} disabled={busy || !password} className="w-full h-11">
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-2" />} Sblocca
              </Button>
              {biometricSupported && (
                <button
                  type="button"
                  data-testid="hr-back-to-biometric"
                  onClick={() => { setPasswordMode(false); setPassword(""); }}
                  className="w-full text-xs text-muted-foreground hover:text-primary underline underline-offset-2 flex items-center justify-center gap-1"
                >
                  <Fingerprint className="h-3 w-3" />
                  Torna all'autenticazione biometrica
                </button>
              )}
            </>
          )}
        </CardContent></Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-widest text-muted-foreground">Risorse Umane</div>
          <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight flex items-center gap-2"><Users className="h-7 w-7 text-primary" />Archivio HR</h1>
          <p className="text-muted-foreground mt-1">Dati dipendenti, contratti, patenti, visite mediche con allegati e scadenziario.</p>
        </div>
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setForm(emptyDip); setEditId(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="hr-add" className="h-11"><Plus className="h-4 w-4 mr-2" /> Nuovo dipendente</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{editId ? "Modifica dipendente" : "Nuovo dipendente"}</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input data-testid="hr-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div><Label>Cognome</Label><Input value={form.cognome} onChange={(e) => setForm({ ...form, cognome: e.target.value })} /></div>
              <div><Label>Codice Fiscale</Label><Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value })} /></div>
              <div><Label>Contratto</Label><Input placeholder="Tempo indeterminato..." value={form.contratto_tipo} onChange={(e) => setForm({ ...form, contratto_tipo: e.target.value })} /></div>
              <div><Label>Telefono</Label><Input value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label htmlFor="hr-contratto-scad">Scadenza contratto</Label><Input id="hr-contratto-scad" data-testid="hr-contratto-scad" type="date" value={form.contratto_scadenza || ""} onChange={(e) => setForm({ ...form, contratto_scadenza: e.target.value })} /></div>
              <div><Label htmlFor="hr-patente-scad">Scadenza patente</Label><Input id="hr-patente-scad" data-testid="hr-patente-scad" type="date" value={form.patente_scadenza || ""} onChange={(e) => setForm({ ...form, patente_scadenza: e.target.value })} /></div>
              <div className="col-span-2"><Label htmlFor="hr-visita-scad">Scadenza visita medica</Label><Input id="hr-visita-scad" data-testid="hr-visita-scad" type="date" value={form.visita_medica_scadenza || ""} onChange={(e) => setForm({ ...form, visita_medica_scadenza: e.target.value })} /></div>
              <div className="col-span-2">
                <Label>Collega a utente dell'app</Label>
                <Select value={form.user_id || "__none__"} onValueChange={(v) => {
                  const next = v === "__none__" ? "" : v;
                  const taken = usersAvailable.find((u) => u.id === next && u.hr_employee_id && u.hr_employee_id !== editId);
                  if (taken && !window.confirm(`${taken.name || taken.email} è già collegato ad un altro dipendente. Vuoi spostare il collegamento qui?`)) return;
                  setForm({ ...form, user_id: next });
                }}>
                  <SelectTrigger data-testid="hr-link-user"><SelectValue placeholder="— nessun collegamento —" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Nessun collegamento —</SelectItem>
                    {usersAvailable.map((u) => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email} · {u.role}
                        {u.hr_employee_id && u.hr_employee_id !== editId ? " · già collegato" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div className="text-[10px] text-muted-foreground mt-1">Collega opzionalmente questa scheda HR all'account applicativo di un operaio o membro del team.</div>
              </div>
              <div className="col-span-2"><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} /></div>
            </div>
            <Button data-testid="hr-save" onClick={save} className="h-11 mt-2">{editId ? "Aggiorna" : "Salva"}</Button>
          </DialogContent>
        </Dialog>
      </div>

      {errorBanner && (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 p-3 flex items-start gap-2" data-testid="hr-error-banner">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-bold text-destructive">Errore di caricamento dati</div>
            <div className="text-xs text-muted-foreground break-words">{errorBanner}</div>
          </div>
          <Button variant="outline" size="sm" onClick={load} data-testid="hr-error-retry">Riprova</Button>
          <button onClick={() => setErrorBanner(null)} className="text-muted-foreground hover:text-foreground text-sm">✕</button>
        </div>
      )}

      {alerts.length > 0 && (
        <Card className="card-elevated border-amber-500"><CardContent className="p-5">
          <div className="flex items-center gap-2 font-bold mb-3"><AlertTriangle className="h-4 w-4 text-amber-600" /> Scadenze imminenti ({alerts.length})</div>
          <div className="grid md:grid-cols-2 gap-2">
            {alerts.map((a, i) => (
              <div key={i} data-testid={`hr-alert-${i}`} className={`p-3 rounded-md text-sm border ${a.severita === "alta" ? "bg-destructive/10 border-destructive/30" : "bg-amber-50 dark:bg-amber-900/20 border-amber-300"}`}>
                <div className="font-bold">{a.dipendente_nome}</div>
                <div className="text-xs">{a.tipo}: {a.scadenza} <span className={a.severita === "alta" ? "text-destructive font-bold" : "text-amber-700"}>({a.giorni_mancanti >= 0 ? `tra ${a.giorni_mancanti}gg` : `scaduto da ${-a.giorni_mancanti}gg`})</span></div>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      <Card className="card-elevated"><CardContent className="p-3 flex flex-wrap gap-2 items-end">
        <div className="flex-1 min-w-[200px]">
          <Label className="text-[10px]">Cerca nome o cognome</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
            <Input data-testid="hr-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Es. Mario Rossi..." className="pl-8 h-9" />
          </div>
        </div>
        <div>
          <Label className="text-[10px]">Filtra per data</Label>
          <Select value={filterColumn} onValueChange={setFilterColumn}>
            <SelectTrigger data-testid="hr-filter-col" className="h-9 w-44 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="contratto_scadenza">Scadenza contratto</SelectItem>
              <SelectItem value="patente_scadenza">Scadenza patente</SelectItem>
              <SelectItem value="visita_medica_scadenza">Scadenza visita medica</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Mese</Label>
          <Select value={String(filterMese)} onValueChange={(v) => setFilterMese(parseInt(v, 10))}>
            <SelectTrigger data-testid="hr-filter-mese" className="h-9 w-28 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Tutti</SelectItem>
              {["Gen","Feb","Mar","Apr","Mag","Giu","Lug","Ago","Set","Ott","Nov","Dic"].map((m, i) => <SelectItem key={i} value={String(i + 1)}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Anno</Label>
          <Select value={String(filterAnno)} onValueChange={(v) => setFilterAnno(parseInt(v, 10))}>
            <SelectTrigger data-testid="hr-filter-anno" className="h-9 w-24 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="0">Tutti</SelectItem>
              {anniDisponibili.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-[10px]">Da</Label>
          <Input data-testid="hr-filter-da" type="date" value={filterDa} onChange={(e) => setFilterDa(e.target.value)} className="h-9 w-36 text-xs" />
        </div>
        <div>
          <Label className="text-[10px]">A</Label>
          <Input data-testid="hr-filter-a" type="date" value={filterA} onChange={(e) => setFilterA(e.target.value)} className="h-9 w-36 text-xs" />
        </div>
        {anyFilter && (
          <Button size="sm" variant="ghost" data-testid="hr-filter-clear" onClick={() => { setSearch(""); setFilterMese(0); setFilterAnno(0); setFilterDa(""); setFilterA(""); }}>
            Pulisci filtri
          </Button>
        )}
        <div className="text-[11px] text-muted-foreground self-center ml-auto">{filteredList.length} di {list.length}</div>
      </CardContent></Card>

      <Card className="card-elevated"><CardContent className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-secondary">
            <tr className="text-left">
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Dipendente</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Contratto</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Patente</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Visita medica</th>
              <th className="px-4 py-3 text-[10px] uppercase tracking-widest text-muted-foreground">Allegati</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map((d) => (
              <tr key={d.id} className="border-t border-border cursor-pointer hover:bg-secondary/40" data-testid={`hr-row-${d.id}`} onClick={(e) => {
                // Non apre il dossier se click su pulsanti o link dentro la riga
                if (e.target.closest("button") || e.target.closest("a") || e.target.closest("input")) return;
                setDetail(d);
              }}>
                <td className="px-4 py-3 font-medium">{d.nome} {d.cognome}<div className="text-[10px] text-muted-foreground">{d.codice_fiscale}</div></td>
                <td className="px-4 py-3 text-xs">{renderContrattoCell(d)}{d.contratto_scadenza && !isIndeterminato(d.contratto_tipo) && <div className="text-muted-foreground">scad: {d.contratto_scadenza}</div>}</td>
                <td className="px-4 py-3 text-xs">{d.patente_scadenza || "—"}</td>
                <td className="px-4 py-3 text-xs">{d.visita_medica_scadenza || "—"}</td>
                <td className="px-4 py-3 text-xs"><button className="hover:text-primary flex items-center gap-1" onClick={() => setDetail(d)} data-testid={`hr-docs-${d.id}`}><FileText className="h-3 w-3" /> {(d.documenti || []).length}</button></td>
                <td className="px-4 py-3 flex gap-1 justify-end">
                  {d.user_id && (
                    <a href="/operai" onClick={(e) => { e.stopPropagation(); }} title="Profilo Operaio collegato" data-testid={`hr-operaio-link-${d.id}`}>
                      <Button variant="ghost" size="icon" className="text-primary"><Users className="h-3 w-3" /></Button>
                    </a>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => openEdit(d)} data-testid={`hr-edit-${d.id}`}><Pencil className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(d.id)}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                </td>
              </tr>
            ))}
            {filteredList.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">{anyFilter ? "Nessun risultato con i filtri attivi." : "Nessun dipendente registrato."}</td></tr>}
          </tbody>
        </table>
      </CardContent></Card>

      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) { setDetail(null); setActiveCat("contratti"); } }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FolderOpen className="h-5 w-5 text-primary" />
              Fascicolo Dipendente — {detail?.nome} {detail?.cognome}
              {detail?.codice_fiscale && <span className="text-xs font-mono text-muted-foreground ml-2">{detail.codice_fiscale}</span>}
            </DialogTitle>
          </DialogHeader>
          {detail && (() => {
            const docsByCat = {};
            CATEGORIES.forEach((c) => { docsByCat[c.key] = []; });
            docsByCat.altro = [];
            (detail.documenti || []).forEach((d) => {
              const k = d.categoria || "altro";
              (docsByCat[k] || docsByCat.altro).push(d);
            });
            return (
              <div className="space-y-4">
                {/* Barra categorie */}
                <div className="grid grid-cols-5 gap-2">
                  {CATEGORIES.map((c) => {
                    const Icon = c.icon;
                    const count = (docsByCat[c.key] || []).length;
                    const active = activeCat === c.key;
                    return (
                      <button
                        key={c.key}
                        type="button"
                        onClick={() => setActiveCat(c.key)}
                        data-testid={`hr-cat-${c.key}`}
                        className={`p-3 rounded-lg border text-left transition ${active ? "border-primary bg-primary/5" : "border-border bg-card hover:bg-secondary"}`}
                      >
                        <div className="flex items-center justify-between">
                          <Icon className={`h-4 w-4 ${active ? "text-primary" : "text-muted-foreground"}`} />
                          {count > 0 && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-primary text-primary-foreground">{count}</span>}
                        </div>
                        <div className={`text-xs font-bold mt-1 ${active ? "text-primary" : ""}`}>{c.label}</div>
                        <div className="text-[9px] text-muted-foreground truncate">{c.desc}</div>
                        {c.aiEnabled && <div className="text-[9px] text-sky-600 flex items-center gap-1 mt-0.5"><Sparkles className="h-2.5 w-2.5" /> AI OCR</div>}
                      </button>
                    );
                  })}
                </div>

                {/* Upload per categoria attiva */}
                <Card className="card-elevated"><CardContent className="p-4 space-y-2">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-bold flex items-center gap-2">
                        <Upload className="h-4 w-4 text-primary" />
                        Carica in: {CAT_BY_KEY[activeCat]?.label}
                      </div>
                      <div className="text-[11px] text-muted-foreground">
                        {CAT_BY_KEY[activeCat]?.aiEnabled
                          ? "Puoi caricare più file. L'AI leggerà il primo per auto-compilare i dati (revisione obbligatoria)."
                          : "Puoi caricare più file contemporaneamente."}
                      </div>
                    </div>
                    {parseBusy && <Loader2 className="h-5 w-5 animate-spin text-primary" />}
                  </div>
                  <input
                    type="file"
                    multiple
                    accept="image/*,application/pdf"
                    onChange={(e) => { uploadFilesToCategory(e.target.files, activeCat); e.target.value = ""; }}
                    data-testid={`hr-upload-${activeCat}`}
                    className="block w-full text-xs file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 file:cursor-pointer"
                  />
                </CardContent></Card>

                {/* Lista doc della categoria attiva */}
                <div>
                  <div className="text-sm font-bold mb-2 flex items-center gap-2">
                    <FileText className="h-4 w-4" />
                    {CAT_BY_KEY[activeCat]?.label} ({(docsByCat[activeCat] || []).length})
                  </div>
                  {(docsByCat[activeCat] || []).length === 0 && (
                    <div className="text-sm text-muted-foreground text-center py-8 border rounded-lg bg-secondary/30">
                      Nessun documento in questa categoria.
                    </div>
                  )}
                  <div className="space-y-2">
                    {(docsByCat[activeCat] || []).map((d) => (
                      <div key={d.id} className="flex items-center gap-2 bg-secondary rounded-lg p-2" data-testid={`hr-doc-${d.id}`}>
                        <Paperclip className="h-3 w-3 text-primary shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{d.nome}</div>
                          <div className="text-[10px] text-muted-foreground">{d.tipo || "—"} {d.scadenza && `· scad: ${d.scadenza}`} · {new Date(d.uploaded_at).toLocaleDateString("it-IT")}</div>
                        </div>
                        <Button variant="ghost" size="sm" onClick={() => setPreviewDoc(d)} data-testid={`hr-doc-preview-${d.id}`}><Eye className="h-3 w-3 mr-1" />Anteprima</Button>
                        <a href={d.file_base64} download={d.nome} target="_blank" rel="noreferrer">
                          <Button variant="ghost" size="sm" data-testid={`hr-doc-download-${d.id}`}><Download className="h-3 w-3 mr-1" />Scarica</Button>
                        </a>
                        <Button variant="ghost" size="icon" onClick={() => removeDoc(d.id)} data-testid={`hr-doc-delete-${d.id}`}><Trash2 className="h-3 w-3 text-destructive" /></Button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Modale Review AI Parsed Data (obbligatoria prima di salvare) */}
      <Dialog open={parsedOpen} onOpenChange={(o) => { if (!o) { setParsedOpen(false); setParsedPreview(null); } }}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-sky-600" />
              {parsedPreview?._ai_failed ? "AI non ha estratto dati — inserimento manuale" : "Dati estratti dall'AI — Rivedi e conferma"}
            </DialogTitle>
            <DialogDescription>
              Il file "{parsedPreview?._source_file || "documento"}" è stato salvato. Rivedi i campi del dipendente qui sotto.
            </DialogDescription>
          </DialogHeader>
          {parsedPreview && (
            <div className="space-y-3">
              <div className={`text-xs rounded p-2 flex items-start gap-2 ${parsedPreview._ai_failed ? "text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200" : "text-sky-700 dark:text-sky-300 bg-sky-50 dark:bg-sky-900/20 border border-sky-200"}`}>
                <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" />
                <span>{parsedPreview._ai_failed ? "L'AI non è riuscito a leggere il documento. Il file è già salvato — compila i campi se vuoi aggiornare l'anagrafica." : "L'AI ha pre-compilato i campi sotto. Modifica se necessario, poi conferma."}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input data-testid="parsed-nome" value={parsedPreview.nome || ""} onChange={(e) => setParsedPreview({ ...parsedPreview, nome: e.target.value })} /></div>
                <div><Label>Cognome</Label><Input data-testid="parsed-cognome" value={parsedPreview.cognome || ""} onChange={(e) => setParsedPreview({ ...parsedPreview, cognome: e.target.value })} /></div>
                <div className="col-span-2"><Label>Codice Fiscale</Label><Input data-testid="parsed-cf" value={parsedPreview.codice_fiscale || ""} onChange={(e) => setParsedPreview({ ...parsedPreview, codice_fiscale: e.target.value.toUpperCase() })} className="font-mono uppercase" /></div>
                <div><Label>Tipo contratto</Label>
                  <Select value={parsedPreview.contratto_tipo || ""} onValueChange={(v) => setParsedPreview({ ...parsedPreview, contratto_tipo: v })}>
                    <SelectTrigger data-testid="parsed-contratto-tipo"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Tempo indeterminato">Tempo indeterminato</SelectItem>
                      <SelectItem value="Tempo determinato">Tempo determinato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Scadenza contratto</Label>
                  <Input type="date" data-testid="parsed-contratto-scad" value={parsedPreview.contratto_scadenza || ""} onChange={(e) => setParsedPreview({ ...parsedPreview, contratto_scadenza: e.target.value })} disabled={parsedPreview.contratto_tipo === "Tempo indeterminato"} />
                </div>
                {parsedPreview.documento_tipo && (
                  <div className="col-span-2 bg-secondary/50 rounded p-2 text-xs">
                    <div className="font-semibold">Documento identità rilevato: <span className="text-primary">{parsedPreview.documento_tipo}</span></div>
                    {parsedPreview.numero_documento && <div>N° {parsedPreview.numero_documento}</div>}
                    {parsedPreview.data_scadenza_documento && <div>Scadenza: {parsedPreview.data_scadenza_documento}</div>}
                  </div>
                )}
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" className="flex-1" onClick={() => { setParsedOpen(false); setParsedPreview(null); }}>Annulla</Button>
                <Button className="flex-1" data-testid="parsed-apply" onClick={applyParsedData}>
                  <ShieldCheck className="h-4 w-4 mr-2" /> Applica al dipendente
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modale Anteprima documento */}
      <Dialog open={!!previewDoc} onOpenChange={(o) => !o && setPreviewDoc(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="truncate">{previewDoc?.nome}</DialogTitle>
          </DialogHeader>
          {previewDoc && (
            <div className="w-full h-[75vh] rounded bg-secondary overflow-hidden">
              {(previewDoc.file_base64 || "").startsWith("data:application/pdf") ? (
                <iframe src={previewDoc.file_base64} className="w-full h-full border-0" title="PDF preview" />
              ) : (
                <img src={previewDoc.file_base64} alt={previewDoc.nome} className="w-full h-full object-contain" />
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
